import {getTaxIdentifier} from '../../AccountInfo/utils/taxIdentifier'

const normalizeStateValue = state => state?.value || state || ''

const hasOwn = (source, key) =>
  Object.prototype.hasOwnProperty.call(source || {}, key)

const pickEditedValue = (editValues, key, fallback = '') =>
  hasOwn(editValues, key) ? editValues[key] : fallback

const pickLocationName = loc => {
  if (hasOwn(loc, 'locationName')) return loc.locationName || ''
  if (hasOwn(loc, 'location_name')) return loc.location_name || ''
  return ''
}

export const buildEditedLocationForCatalog = (loc = {}, editValues = {}) => {
  const currentAddress =
    loc?.address && typeof loc.address === 'object' ? loc.address : {}
  const currentStreet =
    currentAddress.street1 ||
    currentAddress.street ||
    (typeof loc?.address === 'string' ? loc.address : '')
  const streetAddress = pickEditedValue(editValues, 'address', currentStreet)
  const state = normalizeStateValue(
    pickEditedValue(
      editValues,
      'state',
      currentAddress.region || currentAddress.state || loc?.state || '',
    ),
  )
  const postCode = pickEditedValue(
    editValues,
    'postCode',
    currentAddress.postalCode || loc?.postCode || loc?.postalCode || '',
  )
  const country = pickEditedValue(
    editValues,
    'country',
    loc?.country || currentAddress.country || '',
  )
  const company = pickEditedValue(
    editValues,
    'businessName',
    loc?.company || loc?.companyName || loc?.businessName || loc?.name || '',
  )
  const telephone = pickEditedValue(
    editValues,
    'telephone',
    loc?.telephone || loc?.phone || '',
  )
  const mobile = pickEditedValue(editValues, 'mobile', loc?.mobile || '')

  return {
    ...loc,
    email: pickEditedValue(editValues, 'email', loc?.email || ''),
    firstName: pickEditedValue(
      editValues,
      'firstName',
      loc?.firstName || loc?.recurlyFirst || '',
    ),
    lastName: pickEditedValue(
      editValues,
      'lastName',
      loc?.lastName || loc?.recurlyLast || '',
    ),
    company,
    companyName: company || loc?.companyName || '',
    businessName: company || loc?.businessName || '',
    locationName: hasOwn(editValues, 'locationName')
      ? editValues.locationName || ''
      : pickLocationName(loc),
    phone: telephone || mobile || loc?.phone || '',
    telephone,
    mobile,
    billTo: pickEditedValue(editValues, 'billTo', loc?.billTo || ''),
    address: {
      ...currentAddress,
      street1: streetAddress,
      street: streetAddress,
      city: pickEditedValue(
        editValues,
        'city',
        currentAddress.city || loc?.city || '',
      ),
      region: state,
      state,
      postalCode: postCode,
      country,
    },
    city: pickEditedValue(
      editValues,
      'city',
      currentAddress.city || loc?.city || '',
    ),
    state,
    stateCode: state,
    postCode,
    postalCode: postCode,
    country,
  }
}

export const buildKeapPartnerAddLocationCatalogState = ({
  editValues,
  email,
  country,
  thryvId,
  parentCcId,
  keapPartnerId,
}) => {
  const state = normalizeStateValue(editValues.state)
  const postCode = editValues.postCode || ''
  const billTo = editValues.billTo || 'parent'
  const locationName = editValues.locationName || ''
  const businessAddress = {
    street: editValues.address || '',
    city: editValues.city || '',
    state,
    postalCode: postCode,
    phone: editValues.telephone || editValues.mobile || '',
    country,
  }

  return {
    linkType: '1:1',
    selectedCountry: country,
    keapPartnerId,
    isUpgrade: false,
    email,
    accountCode: thryvId,
    parentCcId,
    thryvDetail: {
      thryvId,
      ccId: parentCcId,
      accountAlreadyCreated: true,
      billTo,
    },
    accountInformation: {
      email: email || '',
      firstName: editValues.firstName || '',
      lastName: editValues.lastName || '',
      company: editValues.businessName || '',
      locationName,
      address: businessAddress.street,
      city: businessAddress.city,
      state,
      postCode,
      postalCode: postCode,
      country,
      phone: editValues.telephone || '',
      companyPhone: editValues.telephone || '',
      mobile: editValues.mobile || '',
      billTo,
      businessAddress,
      // Downstream consumers (new-order.js, kp-11Link-generateUrl.js) only ever read
      // accountInformation.abn — nothing reads a separate .nzbn key — so NZ orders must
      // merge in here or the NZBN silently never reaches Recurly.
      abn: getTaxIdentifier(editValues),
    },
    childCustomerInfo: {
      company: editValues.businessName || '',
      firstName: editValues.firstName || '',
      lastName: editValues.lastName || '',
      locationName,
      accountCode: thryvId,
      billTo,
    },
  }
}

export const buildKeapPartnerExistingCatalogState = ({
  loc,
  parentAccount,
  keapPartnerId,
}) => {
  const locationCode = loc?.thryvId || loc?.code || loc?.accountCode
  const parentCcId =
    parentAccount?.thryvId ||
    parentAccount?.accountCode ||
    parentAccount?.code ||
    parentAccount?.id ||
    loc?.parentAccountCode ||
    null
  const postCode =
    loc?.address?.postalCode || loc?.postCode || loc?.postalCode || ''
  const streetAddress =
    loc?.address?.street1 ||
    loc?.address?.street ||
    (typeof loc?.address === 'string' ? loc.address : '')
  const country = loc?.country || parentAccount?.country || 'US'
  const billTo = loc?.billTo || (loc?.parentAccountId ? 'parent' : 'self')
  const locationName = pickLocationName(loc)
  const accountInformation = {
    email: loc?.email || '',
    firstName: loc?.firstName || loc?.recurlyFirst || '',
    lastName: loc?.lastName || loc?.recurlyLast || '',
    company:
      loc?.company || loc?.companyName || loc?.businessName || loc?.name || '',
    locationName,
    address: streetAddress,
    city: loc?.address?.city || loc?.city || '',
    state: loc?.address?.region || loc?.address?.state || loc?.state || '',
    postCode,
    postalCode: postCode,
    country,
    phone: loc?.phone || loc?.telephone || '',
    mobile: loc?.mobile || loc?.phone || '',
    billTo,
  }

  return {
    linkType: '1:1',
    selectedCountry: country,
    keapPartnerId,
    isUpgrade: true,
    email: loc?.email,
    accountCode: locationCode,
    parentCcId,
    thryvDetail: locationCode
      ? {
          thryvId: locationCode,
          ccId: parentCcId,
          accountAlreadyCreated: true,
          billTo,
        }
      : {},
    accountInformation,
    childCustomerInfo: {
      company: accountInformation.company,
      firstName: accountInformation.firstName,
      lastName: accountInformation.lastName,
      locationName,
      accountCode: locationCode,
      billTo,
    },
  }
}
